import { useContext, useEffect, useState } from 'react'
import {
  endOfDay,
  EndOfDayOptions,
  endOfWeek,
  EndOfWeekOptions,
  format,
  isSameYear,
  isToday,
  isTomorrow,
  isYesterday,
  Locale,
  setDefaultOptions,
} from 'date-fns'
import { de, enUS, es, frCA } from 'date-fns/locale'
import { fromZonedTime, toZonedTime } from 'date-fns-tz'

import { AuthenticationContext } from '../../providers/auth-provider'
import { isEuropeanDateFormat, normalizeDateFormatPreference, normalizeTimeFormatPreference, TimeFormat } from '../preference-format'

const getTimezoneAbbreviation = (timezone: string) => {
  try {
    return (
      new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'short',
      })
        .formatToParts()
        .find((part) => part.type === 'timeZoneName')?.value ?? 'UTC'
    )
  } catch {
    return 'UTC'
  }
}

const logDateError = (message: string) => {
  // Keep failures visible in development while preserving graceful fallbacks.
  // eslint-disable-next-line no-console
  console.error(message)
}

// Returns true for a valid IANA timezone string; false for invalid or unsupported values.
export const isValidTimezone = (timezone: string) => {
  try {
    format(toZonedTime(new Date(), timezone), 'MM/dd/yyyy HH:mm:ss')
    return true
  } catch {
    return false
  }
}

// Returns the runtime-detected local timezone, or 'UTC' when unavailable.
export const guessTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'

// Returns the first valid timezone from the provided options, or a guessed local timezone fallback.
export const selectTimezone = (timezoneOptions: (string | undefined)[]) => {
  const firstValidTimezone = timezoneOptions.find((timezone) => timezone && isValidTimezone(timezone))

  if (firstValidTimezone) {
    return firstValidTimezone
  }

  return guessTimezone()
}

const useDates = (timezone?: string) => {
  const { ownUser: user } = useContext(AuthenticationContext)
  const currentLocale = (navigator.language || 'en-US').replace('_', '-').toLowerCase()
  const normalizedDateFormatPreference = normalizeDateFormatPreference(user?.preferences?.dateFormat)
  const prefersEuropeanDateFormat = isEuropeanDateFormat(user?.preferences?.dateFormat)

  const [globalTimezone, setGlobalTimezone] = useState<string>(selectTimezone([timezone, user?.information?.timezone]))
  const [timeFormat, setTimeFormat] = useState<TimeFormat | undefined>(
    user?.preferences?.timeFormat ? normalizeTimeFormatPreference(user.preferences.timeFormat) : undefined
  )

  const timezoneAbbr = getTimezoneAbbreviation(globalTimezone)

  useEffect(() => {
    setGlobalTimezone(selectTimezone([timezone, user?.information?.timezone]))
  }, [user?.information?.timezone, timezone])

  useEffect(() => {
    if (user?.preferences?.timeFormat) {
      setTimeFormat(normalizeTimeFormatPreference(user.preferences.timeFormat))
    }
  }, [user?.preferences?.timeFormat])

  useEffect(() => {
    const localeMap: Record<string, Locale> = {
      en: enUS,
      'en-us': enUS,
      de,
      'fr-ca': frCA,
      es,
    }

    setDefaultOptions({ locale: localeMap[currentLocale] || enUS })
  }, [currentLocale])

  useEffect(() => {
    if (!timeFormat) {
      const testDate = new Date(2023, 0, 1, 14, 14)
      setTimeFormat(format(testDate, 'p').startsWith('14') ? '24hr' : '12hr')
    }
  }, [timeFormat])

  const zonedEndOfDay = (date: string | Date, options?: EndOfDayOptions): Date => {
    const inputZoned = toZonedTime(date, globalTimezone)
    const fnZoned = endOfDay(inputZoned, options)
    return fromZonedTime(fnZoned, globalTimezone)
  }

  const zonedEndOfWeek = (date: string | Date, options?: EndOfWeekOptions): Date => {
    const inputZoned = toZonedTime(date, globalTimezone)
    const fnZoned = endOfWeek(inputZoned, options)
    return fromZonedTime(fnZoned, globalTimezone)
  }

  // Returns a formatted datetime string in the resolved timezone.
  // Fallbacks: String(date) for empty input, parsing errors, or formatting errors.
  // Example: formatWithTimezone('2026-08-25T15:30:00Z', 'MM/dd/yyyy hh:mm a') -> '08/25/2026 10:30 AM' (timezone-dependent)
  const formatWithTimezone = (date: string | Date, dateFormat: string): string => {
    if (!date) return String(date)

    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date
      return format(toZonedTime(dateObj, globalTimezone), dateFormat)
    } catch (error) {
      logDateError(
        `Error formatting date (formatWithTimezone): ${error instanceof Error ? error.toString() : String(error)}; Date: ${date.toString()}; Date Format: ${dateFormat}`
      )
      return date.toString()
    }
  }

  // Returns relative text for near dates ('Today', 'Tomorrow', 'Yesterday')
  // or an absolute calendar date string for other dates.
  // Fallback: String(date) on parsing/formatting failures.
  // Example: formatCalendarDate(new Date()) -> 'Today · August 25' or 'Today · 25 August'
  const formatCalendarDate = (date: string | Date) => {
    if (!date) return String(date)

    try {
      const dateObj = toZonedTime(typeof date === 'string' ? new Date(date) : date, globalTimezone)

      const todayLabel = 'Today · '
      const tomorrowLabel = 'Tomorrow · '
      const yesterdayLabel = 'Yesterday · '

      const prefersEuropeanFormat = prefersEuropeanDateFormat || (!user?.preferences?.dateFormat && timeFormat === '24hr')

      const datePattern = prefersEuropeanFormat ? 'd MMMM' : 'MMMM d'
      const fullYearPattern = prefersEuropeanFormat ? 'd MMMM yyyy' : 'MMMM d, yyyy'

      if (isToday(dateObj)) return todayLabel + format(dateObj, datePattern)
      if (isTomorrow(dateObj)) return tomorrowLabel + format(dateObj, datePattern)
      if (isYesterday(dateObj)) return yesterdayLabel + format(dateObj, datePattern)
      return format(dateObj, isSameYear(dateObj, new Date()) ? datePattern : fullYearPattern)
    } catch (error) {
      logDateError(`Error formatting date (formatCalendarDate): ${error instanceof Error ? error.toString() : String(error)}; Date: ${date.toString()}`)
      return date.toString()
    }
  }

  // Returns relative text for near dates ('Today/Tomorrow/Yesterday at ...')
  // or an absolute datetime string for all other dates.
  // Fallback: String(date) on parsing/formatting failures.
  // Example: formatCalendarDateWithTime(new Date()) -> 'Today at 10:30 AM' or 'today at 10:30'
  const formatCalendarDateWithTime = (date: string | Date, lowercaseNearDate?: boolean) => {
    if (!date) return String(date)

    try {
      const dateObj = toZonedTime(typeof date === 'string' ? new Date(date) : date, globalTimezone)
      const todayLabel = lowercaseNearDate ? 'today at ' : 'Today at '
      const tomorrowLabel = lowercaseNearDate ? 'tomorrow at ' : 'Tomorrow at '
      const yesterdayLabel = lowercaseNearDate ? 'yesterday at ' : 'Yesterday at '

      const calendarTimePattern = timeFormat === '24hr' ? 'HH:mm' : 'hh:mm a'
      const calendarDatePattern = prefersEuropeanDateFormat ? 'dd.MM.yyyy' : 'MM/dd/yyyy'
      const fullDateTimePattern = `${calendarDatePattern} 'at' ${calendarTimePattern}`

      if (isToday(dateObj)) {
        return todayLabel + format(dateObj, calendarTimePattern)
      }
      if (isTomorrow(dateObj)) {
        return tomorrowLabel + format(dateObj, calendarTimePattern)
      }
      if (isYesterday(dateObj)) {
        return yesterdayLabel + format(dateObj, calendarTimePattern)
      }
      return format(dateObj, fullDateTimePattern)
    } catch (error) {
      logDateError(`Error formatting date (formatCalendarDateWithTime): ${error instanceof Error ? error.toString() : String(error)}; Date: ${date.toString()}`)
      return date.toString()
    }
  }

  // Returns a date string using user format preference and resolved timezone.
  // Example: formatDate('2026-08-25T15:30:00Z') -> '08/25/2026' or '25.08.2026'
  const formatDate = (date: string | Date) => {
    return formatWithTimezone(date, prefersEuropeanDateFormat ? 'dd.MM.yyyy' : 'MM/dd/yyyy')
  }

  // Returns a datetime string using user format preference and resolved timezone.
  // When noSeconds is true, seconds are omitted from the output.
  // Example: formatDateTime('2026-08-25T15:30:00Z') -> '08/25/2026 10:30:00 AM' or '25/08/2026 10:30:00'
  // Example: formatDateTime('2026-08-25T15:30:00Z', true) -> '08/25/2026 10:30 AM' or '25/08/2026 10:30'
  const formatDateTime = (date: string | Date, noSeconds = false) => {
    const timePattern = timeFormat === '24hr' ? (noSeconds ? 'HH:mm' : 'HH:mm:ss') : noSeconds ? 'hh:mm a' : 'hh:mm:ss a'
    return formatWithTimezone(date, `${prefersEuropeanDateFormat ? 'dd/MM/yyyy' : 'MM/dd/yyyy'} ${timePattern}`)
  }

  // Returns a short-month datetime string, optionally including the word 'at', in the resolved timezone.
  // Example: formatDateTimeShortMonth('2026-08-25T15:30:00Z', true) -> 'Aug 25, 2026 at 10:30 AM' or '25 Aug 2026 at 10:30'
  const formatDateTimeShortMonth = (date: string | Date, addAt?: boolean) => {
    return formatWithTimezone(
      date,
      `${prefersEuropeanDateFormat ? 'd MMM yyyy' : 'MMM d, yyyy'} ${addAt ? "'at' " : ''}${timeFormat === '24hr' ? 'HH:mm' : 'hh:mm a'}`
    )
  }

  // Returns a short-month date string in the resolved timezone.
  // Example: formatDateShortMonth('2026-08-25T15:30:00Z') -> 'Aug 25, 2026' or '25 Aug 2026'
  const formatDateShortMonth = (date: string | Date) => {
    return formatWithTimezone(date, prefersEuropeanDateFormat ? 'd MMM yyyy' : 'MMM d, yyyy')
  }

  // Returns a full-month date string in the resolved timezone.
  // Example: formatDateLongMonth('2026-08-25T15:30:00Z') -> 'August 25, 2026' or '25 August 2026'
  const formatDateLongMonth = (date: string | Date) => {
    return formatWithTimezone(date, prefersEuropeanDateFormat ? 'd MMMM yyyy' : 'MMMM d, yyyy')
  }

  // Returns day/month text without a year (for example, '3/14' or '14/3').
  // Fallback: String(date) on parsing/formatting failures.
  // Example: formatDateDayMonth('2026-08-25T15:30:00Z') -> '8/25' or '25/8'
  const formatDateDayMonth = (date: string | Date) => {
    if (!date) return String(date)

    try {
      const dateObj = toZonedTime(typeof date === 'string' ? new Date(date) : date, globalTimezone)

      if (prefersEuropeanDateFormat) return format(dateObj, 'd/M')
      if (normalizedDateFormatPreference === 'MM-DD-YYYY') return format(dateObj, 'M/d')

      const fullDate = format(dateObj, 'P')
      const year = format(dateObj, 'yyyy')
      return fullDate.replace(new RegExp(`[.,/-]?${year}`), '').trim()
    } catch (error) {
      logDateError(`Error formatting date (formatDateDayMonth): ${error instanceof Error ? error.toString() : String(error)}; Date: ${date.toString()}`)
      return date.toString()
    }
  }

  // Returns a time string in 12hr/24hr format according to preferences and resolved timezone.
  // Example: formatTime('2026-08-25T15:30:00Z') -> '10:30 AM' or '10:30'; formatTime(date, true) -> '10:30'
  const formatTime = (date: string | Date, noPeriod = false) => {
    return formatWithTimezone(date, timeFormat === '24hr' ? 'HH:mm' : noPeriod ? 'hh:mm' : 'hh:mm a')
  }

  return {
    formatWithTimezone,
    formatCalendarDate,
    formatCalendarDateWithTime,
    formatDate,
    formatDateTime,
    formatTime,
    formatDateTimeShortMonth,
    formatDateShortMonth,
    formatDateLongMonth,
    formatDateDayMonth,
    zonedEndOfDay,
    zonedEndOfWeek,
    timeFormat,
    timezoneAbbr,
    globalTimezone,
  }
}

export default useDates
